/**
 * Shared article/category presentation helpers. Previously these were copy-pasted
 * across hooks, pages, and cards (formatDate ×3, titleCase ×2, slugify ×5,
 * "/og-image.svg" ×5), which invited drift.
 */

/** Fallback image when an article/category has none or its image fails to load. */
export const FALLBACK_IMAGE = "/og-image.svg";

/** Default category slug when an article has no category. */
export const DEFAULT_CATEGORY_SLUG = "lifestyle";

/** Default reading-time label when none is stored. */
export const DEFAULT_READING_TIME = "5 min";

/** Lowercase, hyphenate, strip non-alphanumerics, and trim edge hyphens. */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

/** Format an ISO timestamp as e.g. "January 5, 2026". Empty string for nullish. */
export function formatArticleDate(iso: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Title-case a slug as a last-resort label ("home-decor" → "Home decor"). */
export function titleCaseSlug(slug: string): string {
  return slug.charAt(0).toUpperCase() + slug.slice(1).replace(/-/g, " ");
}

/** Format a date as relative time from today (e.g., "Just now", "2 hours ago", "Yesterday", "3 days ago", "2 weeks ago", "1 month ago"). */
export function formatRelativeTime(dateInput: string | null | undefined): string {
  if (!dateInput) return "";
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) {
    return dateInput;
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "Just now";
  
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes === 1) return "1 min ago";
  if (diffInMinutes < 60) return `${diffInMinutes} mins ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours === 1) return "1 hour ago";
  if (diffInHours < 24) return `${diffInHours} hours ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return "Yesterday";
  if (diffInDays < 7) return `${diffInDays} days ago`;

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks === 1) return "1 week ago";
  if (diffInWeeks < 4) return `${diffInWeeks} weeks ago`;

  const diffInMonths = Math.floor(diffInDays / 30);
  if (diffInMonths === 1) return "1 month ago";
  if (diffInMonths < 12) return `${diffInMonths} months ago`;

  const diffInYears = Math.floor(diffInDays / 365);
  if (diffInYears === 1) return "1 year ago";
  return `${diffInYears} years ago`;
}

export interface ArticleHeading {
  id: string;
  text: string;
  level: number;
}

/**
 * Parses article HTML, guarantees stable IDs and scroll-margin on every heading,
 * and extracts the structured heading tree for the Table of Contents.
 */
export function processArticleHtml(html: string): {
  processedHtml: string;
  headings: ArticleHeading[];
} {
  if (!html) return { processedHtml: "", headings: [] };

  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return { processedHtml: html, headings: [] };
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const headings: ArticleHeading[] = [];
    const seenIds = new Set<string>();

    doc.querySelectorAll("h2, h3").forEach((el, index) => {
      const text = el.textContent?.trim() || "";
      if (!text) return;

      let id = el.getAttribute("id");
      if (!id) {
        id = text
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/(^-|-$)/g, "");
      }
      if (!id || seenIds.has(id)) {
        id = `${id || "section"}-${index + 1}`;
      }
      seenIds.add(id);

      el.setAttribute("id", id);
      el.classList.add("scroll-mt-24");

      headings.push({
        id,
        text,
        level: el.tagName === "H2" ? 2 : 3,
      });
    });

    return {
      processedHtml: doc.body.innerHTML,
      headings,
    };
  } catch {
    return { processedHtml: html, headings: [] };
  }
}
